package org.example.project.enginesmartring

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardNode
import org.example.project.dashboard.ScreenManager
import org.example.project.service.FrameworkUserSettingsService
import org.example.project.service.GlobalManager
import org.example.project.service.LizenzService
import org.example.project.service.RollenService
import org.example.project.utils.All
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.MouseEvent

const val ENGINE_SMARTRING_SCREEN_KEY = "DashboardSmartRing"
const val ENGINE_SMARTRING_SLOT_COUNT = 10

private fun sortEngineSmartRingScreensAlphabetically(screens: List<All>): List<All> =
    screens.sortedWith { left, right ->
        left.label.ifBlank { left.key }.asDynamic().localeCompare(
            right.label.ifBlank { right.key },
            "de",
            js("{ sensitivity: 'base' }")
        ) as Int
    }

private fun resolveStoredRingKey(rawKey: String, allowedScreens: List<All>): String {
    val stored = rawKey.trim()
    if (stored.isBlank() || stored == "__empty__") return ""

    allowedScreens.firstOrNull { it.key == stored }?.let { return it.key }
    allowedScreens.firstOrNull { it.label.equals(stored, ignoreCase = true) }?.let { return it.key }
    return ""
}

private fun resolveStoredRingKeys(columns: List<String>?, allowedScreens: List<All>): List<String> =
    List(ENGINE_SMARTRING_SLOT_COUNT) { index ->
        resolveStoredRingKey(columns?.getOrNull(index).orEmpty(), allowedScreens)
    }

private fun resolveOverlaySlots(
    ringScreenKeys: List<String>,
    availableScreens: List<All>
): List<All?> =
    List(ENGINE_SMARTRING_SLOT_COUNT) { index ->
        ringScreenKeys.getOrNull(index)
            ?.takeIf { it.isNotBlank() }
            ?.let { key -> availableScreens.firstOrNull { it.key == key } }
    }

@Composable
fun EngineSmartRingScreen(art: String = if (GlobalManager.currentFirmenId.isNotBlank()) DashboardNode.CUS else DashboardNode.SYS) {
    val scope = rememberCoroutineScope()
    var ringScreenKeys by remember { mutableStateOf<List<String>>(emptyList()) }
    val userSettingsVersion = FrameworkUserSettingsService.currentUserRingSettingsVersion()
    val resolved = remember(art) { ScreenManager.resolveDashboard(art) }
    val usesCusLizenz = art == DashboardNode.CUS || art == DashboardNode.CUS_MOBILE
    val allowedByLiz = remember(art) {
        if (usesCusLizenz) LizenzService.getEffectiveScreens().toSet() else emptySet()
    }
    val allowedScreens = remember(resolved, art, allowedByLiz) {
        sortEngineSmartRingScreensAlphabetically(
            (resolved.lizenzen + resolved.preise + resolved.module.flatMap { it.columnSettings })
                .filter { screen ->
                    screen.key != ENGINE_SMARTRING_SCREEN_KEY &&
                        (!usesCusLizenz || screen.key in allowedByLiz) &&
                        RollenService.hasReadPermission(screen.key)
                }
                .distinctBy { it.key }
        )
    }

    LaunchedEffect(allowedScreens, userSettingsVersion) {
        FrameworkUserSettingsService.ensureCurrentUserRingSettingsLoaded(forceReload = userSettingsVersion > 0L)
        ringScreenKeys = resolveStoredRingKeys(
            FrameworkUserSettingsService.getUserRingSettings(),
            allowedScreens
        )
    }

    EngineSmartRingSettingsLayout(
        allowedScreens = allowedScreens,
        initialSlots = List(ENGINE_SMARTRING_SLOT_COUNT) { index ->
            ringScreenKeys.getOrNull(index)?.takeIf { it.isNotBlank() }
        },
        onSave = { savedKeys ->
            val nextKeys = List(ENGINE_SMARTRING_SLOT_COUNT) { index ->
                savedKeys.getOrNull(index).orEmpty()
            }
            ringScreenKeys = nextKeys
            scope.launch {
                FrameworkUserSettingsService.saveCurrentUserRingSettings(
                    order = nextKeys,
                    limit = ENGINE_SMARTRING_SLOT_COUNT
                )
            }
        }
    )
}

@Composable
fun EngineSmartRingHost(
    availableScreens: List<All>,
    featureEnabled: Boolean,
    onSlotSelect: (String) -> Unit = {}
) {
    val ringScreenKeys = if (featureEnabled) resolveStoredRingKeys(
        FrameworkUserSettingsService.getUserRingSettings(), availableScreens
    ) else emptyList()
    var ringOverlayAnchor by remember { mutableStateOf<Pair<Double, Double>?>(null) }
    val userSettingsVersion = FrameworkUserSettingsService.currentUserRingSettingsVersion()

    LaunchedEffect(featureEnabled, availableScreens, userSettingsVersion) {
        if (!featureEnabled) {
            ringOverlayAnchor = null
            return@LaunchedEffect
        }

        FrameworkUserSettingsService.ensureCurrentUserRingSettingsLoaded(forceReload = userSettingsVersion > 0L)
    }

    val ringOverlaySlots = remember(ringScreenKeys, availableScreens) {
        resolveOverlaySlots(ringScreenKeys, availableScreens)
    }
    val hasClassicContent = ringOverlaySlots.any { it != null }

    DisposableEffect(ringOverlaySlots, featureEnabled) {
        fun openSmartRing(event: MouseEvent) {
            val target = event.target as? HTMLElement
            val tagName = target?.tagName?.uppercase().orEmpty()
            val isInteractive = tagName in setOf("INPUT", "TEXTAREA", "SELECT", "BUTTON", "A") || (target?.isContentEditable == true)
            if (!featureEnabled || isInteractive) return
            if (!hasClassicContent) return

            event.preventDefault()
            ringOverlayAnchor = event.clientX.toDouble() to event.clientY.toDouble()
        }

        val mouseDownListener: (org.w3c.dom.events.Event) -> Unit = fun(rawEvent) {
            val event = rawEvent as? MouseEvent ?: return
            if (event.button.toInt() != 1) return
            openSmartRing(event)
        }
        val auxClickListener: (org.w3c.dom.events.Event) -> Unit = fun(rawEvent) {
            val event = rawEvent as? MouseEvent ?: return
            if (event.button.toInt() == 1) openSmartRing(event)
        }
        val keyDownListener: (org.w3c.dom.events.Event) -> Unit = fun(rawEvent) {
            val event = rawEvent as? KeyboardEvent ?: return
            if (event.key == "Escape") ringOverlayAnchor = null
        }

        document.addEventListener("mousedown", mouseDownListener)
        document.addEventListener("auxclick", auxClickListener)
        document.addEventListener("keydown", keyDownListener)
        onDispose {
            document.removeEventListener("mousedown", mouseDownListener)
            document.removeEventListener("auxclick", auxClickListener)
            document.removeEventListener("keydown", keyDownListener)
        }
    }

    ringOverlayAnchor?.let { (anchorX, anchorY) ->
        if (featureEnabled && ringOverlaySlots.any { it != null }) {
            EngineSmartRingLayout(
                slots = ringOverlaySlots,
                anchorX = anchorX.coerceIn(180.0, window.innerWidth.toDouble() - 180.0),
                anchorY = anchorY.coerceIn(180.0, window.innerHeight.toDouble() - 180.0),
                onSelect = onSlotSelect,
                onClose = { ringOverlayAnchor = null }
            )
        }
    }
}
